import { supabase } from './supabase';
import { Documento, ArquivoPedido } from '../types';

// ========== SERVIÇO DE DOCUMENTOS ==========

export class DocumentosService {
  // Configurações de validação
  private static readonly TIPOS_PERMITIDOS = [
    'application/pdf',
    'application/xml',
    'text/xml',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  private static readonly TAMANHO_MAXIMO = 10 * 1024 * 1024; // 10MB
  private static readonly TAMANHO_MINIMO = 1024; // 1KB

  // Validar arquivo antes do upload
  static validarArquivo(file: File): { valido: boolean; erro?: string } {
    // Validar tamanho
    if (file.size > this.TAMANHO_MAXIMO) {
      return { valido: false, erro: 'Arquivo muito grande. Máximo permitido: 10MB' };
    }

    if (file.size < this.TAMANHO_MINIMO) {
      return { valido: false, erro: 'Arquivo muito pequeno. Mínimo permitido: 1KB' };
    }

    // Validar tipo
    if (!this.TIPOS_PERMITIDOS.includes(file.type)) {
      return { 
        valido: false, 
        erro: `Tipo de arquivo não permitido. Tipos permitidos: ${this.TIPOS_PERMITIDOS.join(', ')}`
      };
    }

    // Validar nome do arquivo
    if (!file.name || file.name.trim() === '') {
      return { valido: false, erro: 'Nome do arquivo inválido' };
    }

    return { valido: true };
  }

  // Upload de documento para o Supabase Storage
  static async uploadDocumento(
    file: File,
    pasta: string = 'documentos',
    fornecedorMercadoria?: string
  ): Promise<Documento> {
    try {
      // Validar arquivo
      const validacao = this.validarArquivo(file);
      if (!validacao.valido) {
        throw new Error(validacao.erro);
      }

      // Gerar nome único para o arquivo
      const extensao = file.name.split('.').pop();
      const nomeUnico = `${Date.now()}_${crypto.randomUUID()}.${extensao}`;
      const caminho = `${pasta}/${nomeUnico}`;

      // Upload para o Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documentos')
        .upload(caminho, file);

      if (uploadError) throw uploadError;

      // Gerar URL pública
      const { data: urlData } = supabase.storage
        .from('documentos')
        .getPublicUrl(caminho);

      // Criar objeto de documento
      const documento: Documento = {
        id: crypto.randomUUID(),
        nome: file.name,
        tipo: file.type,
        tamanho: file.size,
        fornecedorMercadoria: fornecedorMercadoria || 'Não informado',
        storagePath: caminho,
        uploadedAt: new Date().toISOString(),
        uploadedBy: '' // Será preenchido pelo contexto
      };

      return documento;
    } catch (error) {
      console.error('Erro ao fazer upload do documento:', error);
      throw error;
    }
  }

  // Upload de arquivo de ocorrência
  static async uploadArquivoOcorrencia(file: File, cargaId: string): Promise<{ storagePath: string; url: string }> {
    try {
      // Validar arquivo
      const validacao = this.validarArquivo(file);
      if (!validacao.valido) {
        throw new Error(validacao.erro);
      }

      // Gerar nome único para o arquivo
      const extensao = file.name.split('.').pop();
      const nomeUnico = `ocorrencia_${cargaId}_${Date.now()}_${crypto.randomUUID()}.${extensao}`;
      const caminho = `ocorrencias/${nomeUnico}`;

      // Upload para o Supabase Storage (bucket 'documentos')
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documentos')
        .upload(caminho, file);

      if (uploadError) throw uploadError;

      // Gerar URL pública
      const { data: urlData } = supabase.storage
        .from('documentos')
        .getPublicUrl(caminho);

      return {
        storagePath: caminho,
        url: urlData.publicUrl
      };
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo de ocorrência:', error);
      throw error;
    }
  }

  // Upload de arquivo de pedido
  static async uploadArquivoPedido(
    file: File,
    numeroPedido?: string
  ): Promise<ArquivoPedido> {
    try {
      // Validar arquivo
      const validacao = this.validarArquivo(file);
      if (!validacao.valido) {
        throw new Error(validacao.erro);
      }

      // Gerar nome único para o arquivo
      const extensao = file.name.split('.').pop();
      const nomeUnico = `pedido_${Date.now()}_${crypto.randomUUID()}.${extensao}`;
      const caminho = `pedidos/${nomeUnico}`;

      // Upload para o Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('pedidos')
        .upload(caminho, file);

      if (uploadError) throw uploadError;

      // Gerar URL pública
      const { data: urlData } = supabase.storage
        .from('pedidos')
        .getPublicUrl(caminho);

      // Criar objeto de arquivo de pedido
      const arquivoPedido: ArquivoPedido = {
        id: crypto.randomUUID(),
        databaseKey: caminho,
        nome: file.name,
        tipo: file.type,
        tamanho: file.size,
        numeroPedido,
        storagePath: caminho,
        uploadedAt: new Date().toISOString(),
        uploadedBy: '' // Será preenchido pelo contexto
      };

      return arquivoPedido;
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo de pedido:', error);
      throw error;
    }
  }

  // Download de documento
  static async downloadDocumento(storagePath: string, nomeArquivo: string): Promise<void> {
    try {
      const { data, error } = await supabase.storage
        .from('documentos')
        .download(storagePath);

      if (error) throw error;

      // Criar blob e download
      const blob = new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do documento:', error);
      throw error;
    }
  }

  // Download de arquivo de pedido
  static async downloadArquivoPedido(storagePath: string, nomeArquivo: string): Promise<void> {
    try {
      const { data, error } = await supabase.storage
        .from('pedidos')
        .download(storagePath);

      if (error) throw error;

      // Criar blob e download
      const blob = new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do arquivo de pedido:', error);
      throw error;
    }
  }

  // Visualizar documento (apenas PDF)
  static async visualizarDocumento(storagePath: string): Promise<string> {
    try {
      const { data, error } = await supabase.storage
        .from('documentos')
        .createSignedUrl(storagePath, 3600); // URL válida por 1 hora

      if (error) throw error;

      return data.signedUrl;
    } catch (error) {
      console.error('Erro ao gerar URL de visualização:', error);
      throw error;
    }
  }

  // Visualizar arquivo de pedido (apenas PDF)
  static async visualizarArquivoPedido(storagePath: string): Promise<string> {
    try {
      const { data, error } = await supabase.storage
        .from('pedidos')
        .createSignedUrl(storagePath, 3600); // URL válida por 1 hora

      if (error) throw error;

      return data.signedUrl;
    } catch (error) {
      console.error('Erro ao gerar URL de visualização:', error);
      throw error;
    }
  }

  // Excluir documento do storage
  static async excluirDocumento(storagePath: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from('documentos')
        .remove([storagePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      throw error;
    }
  }

  // Excluir arquivo de pedido do storage
  static async excluirArquivoPedido(storagePath: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from('pedidos')
        .remove([storagePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao excluir arquivo de pedido:', error);
      throw error;
    }
  }

  // Obter URL pública de documento
  static obterUrlPublica(storagePath: string, bucket: string = 'documentos'): string {
    const { data } = supabase.storage.from(bucket).getPublicUrl(storagePath);
    return data.publicUrl;
  }

  // Validar XML (se aplicável)
  static async validarXML(file: File): Promise<{ valido: boolean; erro?: string }> {
    if (file.type !== 'application/xml' && file.type !== 'text/xml') {
      return { valido: true }; // Não é XML, não validar
    }

    try {
      const texto = await file.text();
      
      // Validação básica de XML
      if (!texto.trim().startsWith('<?xml') && !texto.trim().startsWith('<')) {
        return { valido: false, erro: 'Arquivo XML inválido' };
      }

      return { valido: true };
    } catch (error) {
      return { valido: false, erro: 'Erro ao validar XML' };
    }
  }

  // Validar PDF (se aplicável)
  static async validarPDF(file: File): Promise<{ valido: boolean; erro?: string }> {
    if (file.type !== 'application/pdf') {
      return { valido: true }; // Não é PDF, não validar
    }

    try {
      // Validação básica de PDF (verifica se começa com %PDF)
      const texto = await file.text();
      if (!texto.startsWith('%PDF')) {
        return { valido: false, erro: 'Arquivo PDF inválido ou corrompido' };
      }

      return { valido: true };
    } catch (error) {
      return { valido: false, erro: 'Erro ao validar PDF' };
    }
  }

  // Upload múltiplo de documentos
  static async uploadMultiplosDocumentos(
    files: File[],
    pasta: string = 'documentos',
    fornecedorMercadoria?: string
  ): Promise<Documento[]> {
    const documentos: Documento[] = [];

    for (const file of files) {
      try {
        const documento = await this.uploadDocumento(file, pasta, fornecedorMercadoria);
        documentos.push(documento);
      } catch (error) {
        console.error(`Erro ao fazer upload do arquivo ${file.name}:`, error);
        // Continuar com os outros arquivos
      }
    }

    return documentos;
  }
}

export default DocumentosService;
